import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { DollarSign, TrendingUp, CreditCard, Clock, ChevronLeft, ChevronRight, AlertTriangle, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function FinancialPage() {
  const { api, user } = useAuthStore();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_revenue: 0,
    total_renewals: 0,
    total_credits_sold: 0,
    pending_amount: 0
  });

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [custoMensal, setCustoMensal] = useState(null);
  const itemsPerPage = 15;
  
  // Filtros
  const [searchUser, setSearchUser] = useState('');
  const [filterDuplicate, setFilterDuplicate] = useState('all'); // 'all' | 'duplicated' | 'normal'

  useEffect(() => {
    fetchTransactions();
    api.get('/customer-plans/custo-mensal').then(r => setCustoMensal(r.data)).catch(() => {});
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/transactions');
      const txs = response.data;
      setTransactions(txs);
      setCurrentPage(1); // Reset page on filter change
      
      // Calculate stats
      const approved = txs.filter(t => t.status === 'approved');
      const pending = txs.filter(t => t.status === 'pending');
      
      setStats({
        total_revenue: approved
          .filter(t => t.type !== 'credit_purchase' && t.type !== 'master_credit_purchase' && !t.type?.startsWith('nexupay_debit'))
          .reduce((sum, t) => sum + (t.amount || 0), 0),
        total_renewals: approved.filter(t => ['renewal', 'panel_renewal', 'plan_purchase', 'customer_renewal', 'nexupay_debit_renewal'].includes(t.type)).length,
        total_credits_sold: approved.filter(t => ['credit_purchase', 'master_credit_purchase', 'nexupay_debit_credits'].includes(t.type)).reduce((sum, t) => sum + (t.credits_amount || 0), 0),
        pending_amount: pending.reduce((sum, t) => sum + (t.amount || 0), 0)
      });
    } catch (error) {
      toast.error('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      approved: 'bg-green-500/20 text-green-500',
      completed: 'bg-green-500/20 text-green-500',
      pending: 'bg-yellow-500/20 text-yellow-500',
      cancelled: 'bg-red-500/20 text-red-500',
      rejected: 'bg-red-500/20 text-red-500'
    };
    
    const labels = {
      approved: 'Aprovado',
      completed: 'Aprovado',
      pending: 'Pendente',
      cancelled: 'Cancelado',
      rejected: 'Rejeitado'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded ${colors[status] || 'bg-gray-500/20 text-gray-500'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      renewal: 'bg-blue-500/20 text-blue-500',
      plan_purchase: 'bg-blue-500/20 text-blue-500',
      customer_renewal: 'bg-cyan-500/20 text-cyan-500',
      credit_purchase: 'bg-purple-500/20 text-purple-500',
      master_credit_purchase: 'bg-indigo-500/20 text-indigo-500',
      panel_renewal: 'bg-teal-500/20 text-teal-500',
      nexupay_credit: 'bg-emerald-500/20 text-emerald-500',
      nexupay_debit_credits: 'bg-orange-500/20 text-orange-500',
      nexupay_debit_renewal: 'bg-pink-500/20 text-pink-500',
      nexupay_correction: 'bg-red-500/20 text-red-500',
      nexupay_transfer_out: 'bg-blue-500/20 text-blue-500',
      nexupay_transfer_in: 'bg-blue-500/20 text-blue-500',
      nexupay_adjustment: 'bg-amber-500/20 text-amber-500',
      nexupay_commission: 'bg-emerald-500/20 text-emerald-500',
      store_purchase: 'bg-violet-500/20 text-violet-500',
      integration_slot: 'bg-sky-500/20 text-sky-500',
    };
    
    const labels = {
      renewal: 'Renovação',
      plan_purchase: 'Compra de Plano',
      customer_renewal: 'Renovação Cliente',
      credit_purchase: 'Compra de Créditos',
      master_credit_purchase: 'Créditos (Master)',
      panel_renewal: 'Renovação Painel',
      nexupay_credit: 'NexuPay Recebido',
      nexupay_debit_credits: 'NexuPay Compra Créditos',
      nexupay_debit_renewal: 'NexuPay Renovação',
      nexupay_correction: 'NexuPay Correção',
      nexupay_transfer_out: 'NexuPay Transf. Enviada',
      nexupay_transfer_in: 'NexuPay Transf. Recebida',
      nexupay_adjustment: 'NexuPay Ajuste Admin',
      nexupay_commission: 'Comissão Vendedor',
      store_purchase: 'Compra na Loja',
      integration_slot: 'Integração Extra',
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded ${colors[type] || 'bg-gray-500/20 text-gray-500'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const getProviderBadge = (provider) => {
    const colors = {
      mercadopago: 'bg-sky-500/20 text-sky-500',
      fastdepix: 'bg-orange-500/20 text-orange-500',
      nexupay: 'bg-emerald-500/20 text-emerald-500',
      ativopay: 'bg-purple-500/20 text-purple-500',
      rocketpay: 'bg-red-500/20 text-red-500',
    };
    
    const labels = {
      mercadopago: 'MercadoPago',
      fastdepix: 'FastDePix',
      nexupay: 'NexuPay',
      ativopay: 'AtivoPay',
      rocketpay: 'RocketPay',
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded ${colors[provider] || 'bg-gray-500/20 text-gray-500'}`}>
        {labels[provider] || provider || '-'}
      </span>
    );
  };

  // Filtrar transações
  const filteredTransactions = transactions.filter(tx => {
    if (searchUser && !(tx.username || '').toLowerCase().includes(searchUser.toLowerCase()) && !(tx.customer_name || '').toLowerCase().includes(searchUser.toLowerCase())) return false;
    if (filterDuplicate === 'duplicated' && !tx.duplicate_flag) return false;
    if (filterDuplicate === 'normal' && tx.duplicate_flag) return false;
    return true;
  });

  // Paginação
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);
  const duplicateCount = transactions.filter(t => t.duplicate_flag).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Financeiro</h1>
          <p className="text-muted-foreground">Histórico de transações e estatísticas</p>
        </div>

        {/* Filtro removido — admin sempre vê todas */}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Faturamento Total</p>
              <p className="text-2xl font-bold text-green-500">R$ {stats.total_revenue.toFixed(2)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Renovações</p>
              <p className="text-2xl font-bold text-blue-500">{stats.total_renewals}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Créditos Vendidos</p>
              <p className="text-2xl font-bold text-purple-500">{stats.total_credits_sold}</p>
            </div>
            <CreditCard className="w-8 h-8 text-purple-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pagamentos Pendentes</p>
              <p className="text-2xl font-bold text-yellow-500">R$ {stats.pending_amount.toFixed(2)}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500 opacity-50" />
          </div>
        </Card>

        {custoMensal?.tem_custo_configurado && (
          <Card className="p-6" title="Custo mensal com base nos planos que você configurou o custo × clientes ativos vinculados">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Meu Custo Mensal</p>
                <p className="text-2xl font-bold text-orange-500">R$ {(custoMensal.total || 0).toFixed(2)}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{custoMensal.detalhes?.reduce((a, d) => a + d.clientes, 0) || 0} cliente(s) em planos com custo</p>
              </div>
              <TrendingUp className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </Card>
        )}
      </div>

      {custoMensal?.detalhes?.length > 0 && (
        <Card className="p-5">
          <p className="text-sm font-semibold mb-3 flex items-center gap-1.5">💰 Detalhamento do custo mensal</p>
          <div className="space-y-2">
            {custoMensal.detalhes.map((d) => (
              <div key={d.plano_id} className="flex items-center justify-between text-sm border-b border-border/40 pb-2 last:border-0">
                <div>
                  <span className="font-medium">{d.plano_nome}</span>
                  <span className="text-muted-foreground text-xs ml-2">{d.clientes} × R$ {d.custo_unitario.toFixed(2)}</span>
                </div>
                <span className="font-semibold text-orange-500">R$ {d.subtotal.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 font-bold">
              <span>Total mensal</span>
              <span className="text-orange-500">R$ {(custoMensal.total || 0).toFixed(2)}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Transactions Table */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">Historico de Transacoes</h2>
          <p className="text-sm text-muted-foreground">
            {filteredTransactions.length} transacao{filteredTransactions.length !== 1 ? 'es' : ''}
            {duplicateCount > 0 && <span className="text-amber-400 ml-1">({duplicateCount} duplicada{duplicateCount > 1 ? 's' : ''})</span>}
          </p>
        </div>
        
        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por revenda ou cliente..."
              value={searchUser}
              onChange={(e) => { setSearchUser(e.target.value); setCurrentPage(1); }}
              className="pl-9 h-9"
            />
          </div>
          <Select value={filterDuplicate} onValueChange={(v) => { setFilterDuplicate(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="duplicated">Duplicadas</SelectItem>
              <SelectItem value="normal">Sem duplicatas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {filteredTransactions.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 text-sm font-medium text-muted-foreground"></th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Data</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Tipo</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Usuario</th>
                    {user?.role === 'admin' && (
                      <th className="pb-3 text-sm font-medium text-muted-foreground">Master</th>
                    )}
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Gateway</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Valor</th>
                    <th className="pb-3 text-sm font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((tx) => (
                    <tr key={tx.id} className={`border-b last:border-0 hover:bg-muted/50 transition-colors ${tx.duplicate_flag ? 'bg-amber-500/5' : ''}`}>
                      <td className="py-3 w-8">
                        {tx.duplicate_flag && (
                          <span title="Transacao duplicada - creditou NexuPay e gateway externo">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-sm">
                        {new Date(tx.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="py-3">
                        {getTypeBadge(tx.type)}
                      </td>
                      <td className="py-3 text-sm">
                        {tx.type === 'customer_renewal' && tx.customer_name 
                          ? tx.customer_name 
                          : (tx.username || 'N/A')}
                      </td>
                      {user?.role === 'admin' && (
                        <td className="py-3 text-sm">
                          {tx.master_username || '-'}
                        </td>
                      )}
                      <td className="py-3">
                        <div className="flex flex-col gap-1 items-start">
                          {getProviderBadge(tx.provider)}
                          {tx.metodo === 'cartao' ? (
                            <span className="px-2 py-0.5 text-[10px] rounded bg-indigo-500/20 text-indigo-400 flex items-center gap-1">💳 Cartão</span>
                          ) : (
                            <span className="px-2 py-0.5 text-[10px] rounded bg-teal-500/20 text-teal-400 flex items-center gap-1">⚡ PIX</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 font-bold text-sm">
                        R$ {tx.amount.toFixed(2)}
                        {tx.credits_amount > 0 && (
                          <span className="block text-xs font-normal text-purple-400">{tx.credits_amount} créditos</span>
                        )}
                      </td>
                      <td className="py-3">
                        {getStatusBadge(tx.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Mostrando {startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} de {filteredTransactions.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (currentPage <= 3) {
                        page = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className="w-8 h-8 p-0"
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-muted-foreground py-8">Nenhuma transação encontrada</p>
        )}
      </Card>
    </div>
  );
}
